import React from 'react';
import { Card, Statistic, Tag, Space, Typography, Button } from 'antd';
import AiAgreementIndicator from './AiAgreementIndicator';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';
import { maturityBandLabel } from '../utils/maturity';
import MaturityDomainBreakdown from './MaturityDomainBreakdown';
import { DASHBOARD_STAGE_NAMES } from '../constants/dashboardTiles';
import { RadialGauge } from './charts';

const { Paragraph, Text } = Typography;

/**
 * One dashboard tile card. Shared, verbatim, by BOTH ProjectDashboard.jsx (in-app,
 * MSAL-authenticated) and SharedDashboardPage.jsx (public, unauthenticated) — this is
 * deliberate, not incidental reuse: it is the mechanism that satisfies the 2nd Devil's Advocate
 * pass's HARD BUILD CONDITION that every `ai_synthesized` tile's `confidence` + `aiReviewLabel`
 * reach display on BOTH surfaces, never dropped on one of the two paths (see
 * .claude/design/dashboard-redesign.md §A.5 and
 * .claude/lessons/ai-response-dto-drops-confidence-reasoningrefs.md — already 2 prior
 * occurrences of exactly this drop in this codebase). Do not fork a second tile renderer for the
 * public page; add a prop here instead if the two surfaces ever need to diverge.
 *
 * `tile` shape (the in-app DTO, `DashboardTileView` in the backend's dashboardRead.ts; the public
 * shared-link payload is a subset of it):
 *   { tileId, title?, category?, value, sourceType: 'computed'|'ai_synthesized',
 *     computedAt?: string|null, confidence?: number|null, aiReviewedAt?: string, aiReviewLabel?: string,
 *     confidenceDetail?: { confidenceRuns, validSamples, reasoningRefs, confidenceUnavailableReason }|null,
 *     availability?: string }
 * Run-agreement detail is read from the nested `confidenceDetail` and falls back to the same fields
 * flat on the tile, so a payload shaped either way renders identically.
 *
 * `branding` (optional, { primaryColor, fontFamily } from utils/dashboardBranding.js) tints the card
 * with the company's colour/font (Commandment VIII); absent means no tinting, never a made-up default.
 */

function formatTileValue(value) {
  if (value == null) return { kind: 'empty' };
  if (typeof value === 'number') return { kind: 'number', number: value };
  if (typeof value === 'string') return { kind: 'text', text: value };
  if (typeof value === 'object') {
    if (typeof value.percent === 'number') return { kind: 'percent', number: value.percent };
    if (typeof value.days === 'number') return { kind: 'days', number: value.days };
    if (typeof value.count === 'number') return { kind: 'number', number: value.count };
    if (typeof value.text === 'string') return { kind: 'text', text: value.text };
    // AI tile payloads, exactly as the backend writes them (services/dashboard/aiTiles.ts).
    if (typeof value.maturityScore === 'number') {
      return {
        kind: 'maturity',
        score: value.maturityScore,
        band: typeof value.maturityBand === 'string' ? value.maturityBand : null,
        justification: typeof value.justification === 'string' && value.justification.trim() ? value.justification : null,
        domains: Array.isArray(value.domains) ? value.domains : null,
      };
    }
    if (typeof value.narrative === 'string' && value.narrative.trim()) return { kind: 'narrative', text: value.narrative };
  }
  return { kind: 'unrecognised' };
}

/**
 * Commandment IV: confidence score + explainability must accompany every AI output, on every
 * surface it's rendered. `confidence` and `aiReviewLabel` are rendered as two SEPARATE badges
 * (never collapsed into one, and never silently omitted when `confidence` is null) — the
 * `aiReviewLabel` string is a server-computed, human-readable summary (already includes the
 * "confidence not available" fallback wording per §A.5 when `confidence` is null); the numeric
 * confidence badge is this component's own rendering of the same field, so a reviewer can see
 * the raw number too, not just the label's prose.
 */
function AiProvenance({ confidence, runs, validSamples, reasoningRefs, unavailableReason, aiReviewLabel }) {
  // A tile only reaches the value branch after the server's approval gate passed (an unapproved AI
  // value is withheld, never sent), so the "review manually before approving" prompt - which belongs
  // to the approval modal - is not shown on a published tile.
  return (
    <Space direction="vertical" size={4}>
      <Tag color="purple">AI-synthesized</Tag>
      <AiAgreementIndicator
        confidence={confidence}
        runs={runs}
        validSamples={validSamples}
        reasoningRefs={reasoningRefs}
        unavailableReason={unavailableReason}
        approved
      />
      {aiReviewLabel && (
        <Tag data-testid="ai-review-label" color="default">
          {aiReviewLabel}
        </Tag>
      )}
    </Space>
  );
}

/**
 * Withheld tiles: the backend could not honestly compute a value, so it sends `value: null` and
 * an `availability` state (decided server-side: staff get the precise state, company personas
 * and the public shared link only ever get `withheld`; this component never decides who sees
 * which reason). The tile keeps its title so it never looks like the dashboard is broken, and
 * explains itself in plain language instead of showing a zero, a blank card, or a spinner.
 *
 * `refreshOutcome` (optional, staff-only) is the `skippedReason` from this session's Refresh
 * result; it only sharpens the copy for a tile that is still withheld. The moment a snapshot
 * exists the server reports `available` and the normal value rendering below takes over. No
 * tile ids are hardcoded here, so a tile flipping from withheld to emitted needs no code change.
 */
const WITHHELD_COPY = {
  awaiting_approval: {
    label: 'Awaiting approval',
    text: 'This AI-written summary is shown only after a Lead Advisor has reviewed and approved it.',
  },
  needs_more_data: {
    label: 'Needs more data',
    text: "This summary relies on information PolicyLense doesn't record yet, so none is shown rather than a guess.",
  },
  not_wired: {
    label: 'Not connected yet',
    text: "This measure isn't calculated from project data yet, so no figure is shown.",
  },
  not_calculated: {
    label: 'Not calculated yet',
    text: 'It will appear after the next dashboard refresh.',
  },
  // Staff-only: the tile's workflow stage has not been reached. Neutral, never a zero or an empty chart.
  stage_not_reached: {
    label: 'Not started yet',
    text: 'Available once this stage of the project is reached.',
  },
  withheld: {
    label: 'Not available',
    text: 'This measure is not available in this report.',
  },
};

/** "Not started yet: available once <stage name> is reached" for a stage_not_reached tile. */
export function stageNotReachedText(tile) {
  const stage = tile?.stage?.number ?? tile?.requiresStage;
  // Gap analysis completes only when the Lead Advisor pushes the SPOC-approved report (workflow slide 8).
  if (stage === 4) return 'Not started yet: available once the Lead Advisor pushes the SPOC-approved report.';
  const name = tile?.stage?.name || DASHBOARD_STAGE_NAMES[stage];
  return name ? `Not started yet: available once ${name} is reached.` : WITHHELD_COPY.stage_not_reached.text;
}

export function resolveWithheldKind(tile, refreshOutcome) {
  const availability = tile?.availability;
  if (!availability || availability === 'available') return null;
  // The server is the authority. A session refresh outcome may only sharpen `not_calculated`
  // (a tile with no awaiting-approval claim); it never touches any other state, so a stale
  // outcome can never contradict what the server says now.
  if (availability === 'not_calculated' && refreshOutcome && refreshOutcome.wrote === false) {
    if (refreshOutcome.skippedReason === 'MISSING_REQUIRED_INPUT') return 'needs_more_data';
    if (refreshOutcome.skippedReason === 'NOT_WIRED') return 'not_wired';
  }
  // Unknown/new availability values fall back to the neutral copy, never to a stale claim.
  return WITHHELD_COPY[availability] ? availability : 'withheld';
}

/** Staff-only entry into the approval flow. Rendered only when the page passes
 * `onManageApproval` (Lead Advisor / admin on the in-app dashboard) for an AI tile that can be
 * reviewed; the API is the real gate. `needs_more_data` has nothing to approve, and the
 * client-safe `withheld` state never reaches a staff page. */
function ManageApprovalButton({ tile, onManageApproval, withheldKind, disabledReason }) {
  if (!onManageApproval || tile.computeType !== 'ai_synthesis') return null;
  if (withheldKind === 'needs_more_data' || withheldKind === 'withheld' || withheldKind === 'stage_not_reached') return null;
  const label = withheldKind === 'awaiting_approval' ? 'Review and approve' : 'Manage approval';
  // `disabledReason` (e.g. impersonation) is decided by the page; the server still refuses.
  return (
    <>
      <Button
        size="small"
        data-testid="tile-manage-approval"
        disabled={!!disabledReason}
        title={disabledReason || undefined}
        onClick={() => onManageApproval(tile)}
      >
        {label}
      </Button>
      {disabledReason ? (
        <Text type="secondary" data-testid="tile-approval-disabled-reason">
          {disabledReason}
        </Text>
      ) : null}
    </>
  );
}

const DashboardTile = ({ tile, refreshOutcome, onManageApproval, approvalDisabledReason, branding }) => {
  if (!tile) return null;
  const withheldKind = resolveWithheldKind(tile, refreshOutcome);
  const cardStyle = branding
    ? {
        ...(branding.primaryColor ? { borderTop: `3px solid ${branding.primaryColor}` } : {}),
        ...(branding.fontFamily ? { fontFamily: branding.fontFamily } : {}),
      }
    : undefined;
  if (withheldKind) {
    const copy = WITHHELD_COPY[withheldKind];
    return (
      <Card size="small" style={cardStyle} data-testid={`dashboard-tile-${tile.tileId}`} title={tile.title || tile.tileId}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Tag data-testid="tile-withheld-label">{copy.label}</Tag>
          <Text type="secondary" data-testid="tile-withheld-reason">
            {withheldKind === 'stage_not_reached' ? stageNotReachedText(tile) : copy.text}
          </Text>
          <ManageApprovalButton tile={tile} onManageApproval={onManageApproval} withheldKind={withheldKind} disabledReason={approvalDisabledReason} />
        </Space>
      </Card>
    );
  }
  const isAi = tile.sourceType === 'ai_synthesized';
  const parsed = formatTileValue(tile.value);
  const detail = tile.confidenceDetail || {};

  return (
    <Card size="small" style={cardStyle} data-testid={`dashboard-tile-${tile.tileId}`} title={tile.title || tile.tileId}>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {parsed.kind === 'number' && <Statistic value={parsed.number} />}
        {parsed.kind === 'percent' && <RadialGauge value={parsed.number} label={tile.title || tile.tileId} testId="tile-gauge" />}
        {parsed.kind === 'days' && <Statistic value={parsed.number} suffix=" days" />}
        {parsed.kind === 'text' && <Paragraph style={{ marginBottom: 0 }}>{parsed.text}</Paragraph>}
        {parsed.kind === 'maturity' && (
          <>
            <RadialGauge value={parsed.score} max={100} unit=" / 100" label={tile.title || 'Maturity score'} testId="tile-gauge" />
            {parsed.band && <Tag data-testid="tile-maturity-band">Maturity band: {maturityBandLabel(parsed.band)}</Tag>}
            {parsed.justification && (
              <Paragraph data-testid="tile-justification" style={{ marginBottom: 0 }}>
                {parsed.justification}
              </Paragraph>
            )}
            <MaturityDomainBreakdown domains={parsed.domains} />
          </>
        )}
        {parsed.kind === 'narrative' && (
          <Paragraph data-testid="tile-narrative" style={{ marginBottom: 0 }}>
            {parsed.text}
          </Paragraph>
        )}
        {parsed.kind === 'unrecognised' && <Text type="secondary">This value could not be displayed.</Text>}
        {parsed.kind === 'empty' && <Text type="secondary">No data yet.</Text>}
        {isAi && (
          <AiProvenance
            confidence={tile.confidence}
            runs={detail.confidenceRuns ?? tile.confidenceRuns}
            validSamples={detail.validSamples ?? tile.validSamples}
            reasoningRefs={detail.reasoningRefs ?? tile.reasoningRefs}
            unavailableReason={detail.confidenceUnavailableReason ?? tile.confidenceUnavailableReason}
            aiReviewLabel={tile.aiReviewLabel}
          />
        )}
        {tile.computedAt && (
          <Text type="secondary" data-testid="tile-as-of">
            As of {formatDdMmYyyy(tile.computedAt)}
          </Text>
        )}
        <ManageApprovalButton tile={tile} onManageApproval={onManageApproval} withheldKind={null} disabledReason={approvalDisabledReason} />
      </Space>
    </Card>
  );
};

export default DashboardTile;
